import { Box, FormControlLabel, Grid, Switch } from '@mui/material'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useGetMeasureUnitsQuery } from '../../../app/redux-fetch/apiCoreQuery'
import { convertToLabelValue } from '../../../app/utils'
import { IMeasureUnit } from '../../../features/LoadTemplate/types'
import messages from '../../../features/Orders/NewShipment/messages'
import { LoadType } from '../../../types/common'
import DefaultSpacingGrid from '../../Containers/DefaultSpacingGrid'
import SwitchWithLabels from '../../Switches/SwitchWithLabels'
import InfoNumberDisplayItem from '../../Typographies/InfoNumberDisplayItem'
import CombinedSelect from '../CombinedSelect'
import FormInput from '../FormInput'

interface PhysicalPropertiesProps {
  prefix?: string
  loadTypes?: { id: number; name: string }[]
  isDisabled?: boolean
  isPayloadWeightDisabled?: boolean
  loadTypeId?: number
  loadTemplateId?: number
  isVolume?: boolean
  showInternal?: boolean
  variant?:
  | 'load'
  | 'asset'
  | 'product'
  | 'packaging'
  | 'template'
  | 'loadTemplate'
  | 'shipmentSummary'
  | 'contentSummary'
}

const SharedPhysicalProperties: React.FC<PhysicalPropertiesProps> = ({
  prefix: rawPrefix = '',
  loadTypes = [],
  isDisabled = false,
  isPayloadWeightDisabled = false,
  loadTypeId: passedLoadTypeId,
  loadTemplateId,
  isVolume = false,
  showInternal = false,
  variant = 'load',
}) => {
  const { watch, setValue, control } = useFormContext()
  const { formatMessage } = useIntl()
  const skipMeasureUnits = variant === 'product' && isDisabled
  const { data: measureUnits = [] } = useGetMeasureUnitsQuery(undefined, { skip: skipMeasureUnits })
  const prefix = rawPrefix ? `${rawPrefix}.` : ''
  const watchedLoadTypeId = watch(`${prefix}loadTypeId`)
  const packagingTemplateId = watch(`${prefix}packagingTemplateId`)
  const physicalProperties = watch(`${prefix}physicalProperties`) ?? {}

  const {
    useDimensions,
    isRectangular,
    internalUseDimensions,
    externalUseDimensions,
    internalIsRectangular,
    externalIsRectangular,
    loadType: physicalPropertiesLoadType,
    lengthMeasureUnitId,
    weightMeasureUnitId,
    netWeight,
    grossWeight,
  } = physicalProperties

  // Compute effective toggle values synchronously from physical properties data so the
  // correct rendering branch (Volume/Cylindrical/Rectangle) shows on the FIRST render.
  // This prevents the Length CombinedSelect from mounting late and losing its form value
  // (the symptom: the field appears empty until the user navigates away and back).
  const effectiveUseDimensions = useMemo(() => {
    if (variant === 'asset') {
      const sectionUseDims = showInternal ? internalUseDimensions : externalUseDimensions
      if (sectionUseDims !== undefined) return sectionUseDims
      const dimPrefix = showInternal ? 'internal' : 'external'
      const hasDims = Boolean(
        physicalProperties?.[`${dimPrefix}Length`] || physicalProperties?.[`${dimPrefix}Width`] || physicalProperties?.[`${dimPrefix}Height`]
      )
      const hasVol = Boolean(physicalProperties?.[`${dimPrefix}Volume`])
      return hasDims || (!hasDims && !hasVol)
    }
    if (useDimensions !== undefined) return useDimensions
    const hasDims = Boolean(
      physicalProperties?.length || physicalProperties?.width || physicalProperties?.height
    )
    const hasVol = Boolean(physicalProperties?.volume)
    return hasDims || (!hasDims && !hasVol)
  }, [variant, useDimensions, showInternal, internalUseDimensions, externalUseDimensions, physicalProperties])

  const effectiveIsRectangular = useMemo(() => {
    if (variant === 'asset') {
      const sectionIsRect = showInternal ? internalIsRectangular : externalIsRectangular
      if (sectionIsRect !== undefined) return sectionIsRect
      const dimPrefix = showInternal ? 'internal' : 'external'
      const hasWidth = Boolean(physicalProperties?.[`${dimPrefix}Width`])
      return hasWidth || !physicalProperties?.[`${dimPrefix}Length`]
    }
    if (isRectangular !== undefined) return isRectangular
    const hasWidth = Boolean(physicalProperties?.width)
    return hasWidth || !physicalProperties?.length
  }, [variant, isRectangular, showInternal, internalIsRectangular, externalIsRectangular, physicalProperties])

  const selectedLoadTypeId = passedLoadTypeId ?? watchedLoadTypeId ?? physicalPropertiesLoadType?.id

  const isParcel = useMemo(() => {
    if (!selectedLoadTypeId) return false
    const loadType = loadTypes.find((lt) => lt.id === selectedLoadTypeId)
    return loadType?.name.toLowerCase() === 'parcel' || loadType?.name.toLowerCase() === 'crate'
  }, [loadTypes, selectedLoadTypeId])

  const isCrate = useMemo(() => {
    if (!selectedLoadTypeId) return false
    const loadType = loadTypes.find((lt) => lt.id === selectedLoadTypeId)
    return loadType?.name.toLowerCase() === 'crate'
  }, [loadTypes, selectedLoadTypeId])

  const isPallet = useMemo(() => {
    if (!selectedLoadTypeId) return false
    const loadType = loadTypes.find((lt) => lt.id === selectedLoadTypeId)
    return loadType?.name.toLowerCase() === 'pallet'
  }, [loadTypes, selectedLoadTypeId])

  useEffect(() => {
    if (lengthMeasureUnitId) {
      setValue(`${prefix}physicalProperties.lengthMeasureUnitId`, lengthMeasureUnitId)
    }
  }, [lengthMeasureUnitId, prefix, setValue])

  useEffect(() => {
    if (weightMeasureUnitId) {
      setValue(`${prefix}physicalProperties.weightMeasureUnitId`, weightMeasureUnitId)
    }
  }, [weightMeasureUnitId, prefix, setValue])

  useEffect(() => {
    if (typeof useDimensions === 'undefined') {
      const hasDimensions = Boolean(
        physicalProperties?.length || physicalProperties?.width || physicalProperties?.height ||
        physicalProperties?.externalLength || physicalProperties?.externalWidth || physicalProperties?.externalHeight ||
        physicalProperties?.internalLength || physicalProperties?.internalWidth || physicalProperties?.internalHeight
      )
      const hasVolumeOrDensity = Boolean(
        physicalProperties?.volume || physicalProperties?.density ||
        physicalProperties?.externalVolume || physicalProperties?.internalVolume
      )
      const initialUseDimensions = hasDimensions || (!hasDimensions && !hasVolumeOrDensity)
      setValue(`${prefix}physicalProperties.useDimensions`, initialUseDimensions, {
        shouldValidate: true,
      })
    }
  }, [useDimensions, physicalProperties?.length, physicalProperties?.width, physicalProperties?.height, physicalProperties?.externalLength, physicalProperties?.externalWidth, physicalProperties?.externalHeight, physicalProperties?.internalLength, physicalProperties?.internalWidth, physicalProperties?.internalHeight, physicalProperties?.volume, physicalProperties?.density, physicalProperties?.externalVolume, physicalProperties?.internalVolume, prefix, setValue])

  // Auto-detect per-section toggle values for asset variant
  // IMPORTANT: Both useDimensions and isRectangular must be set atomically in the same effect
  // to prevent an intermediate render where useDimensions=true but isRectangular=undefined,
  // which would briefly render the Cylindrical branch and cause the Length CombinedSelect to
  // mount/unmount/remount, losing its form value.
  useEffect(() => {
    if (variant !== 'asset') return
    if (typeof internalUseDimensions === 'undefined') {
      const hasInternalDims = Boolean(
        physicalProperties?.internalLength || physicalProperties?.internalWidth || physicalProperties?.internalHeight
      )
      const hasInternalVolume = Boolean(physicalProperties?.internalVolume)
      const initialUseDims = hasInternalDims || (!hasInternalDims && !hasInternalVolume)
      setValue(`${prefix}physicalProperties.internalUseDimensions`, initialUseDims, { shouldValidate: true })
      // Set isRectangular in the same batch to avoid intermediate Cylindrical render
      const hasWidth = Boolean(physicalProperties?.internalWidth)
      setValue(`${prefix}physicalProperties.internalIsRectangular`, hasWidth || !physicalProperties?.internalLength)
    }
    if (typeof externalUseDimensions === 'undefined') {
      const hasExternalDims = Boolean(
        physicalProperties?.externalLength || physicalProperties?.externalWidth || physicalProperties?.externalHeight
      )
      const hasExternalVolume = Boolean(physicalProperties?.externalVolume)
      const initialUseDims = hasExternalDims || (!hasExternalDims && !hasExternalVolume)
      setValue(`${prefix}physicalProperties.externalUseDimensions`, initialUseDims, { shouldValidate: true })
      // Set isRectangular in the same batch to avoid intermediate Cylindrical render
      const hasWidth = Boolean(physicalProperties?.externalWidth)
      setValue(`${prefix}physicalProperties.externalIsRectangular`, hasWidth || !physicalProperties?.externalLength)
    }
  }, [variant, internalUseDimensions, externalUseDimensions, physicalProperties?.internalLength, physicalProperties?.internalWidth, physicalProperties?.internalHeight, physicalProperties?.internalVolume, physicalProperties?.externalLength, physicalProperties?.externalWidth, physicalProperties?.externalHeight, physicalProperties?.externalVolume, prefix, setValue])

  useEffect(() => {
    if (packagingTemplateId !== -1) {
      return
    }
    const tare = (Number(grossWeight) || 0) - (Number(netWeight) || 0)
    setValue(`${prefix}physicalProperties.tareWeight`, tare > 0 ? tare : 0)
  }, [grossWeight, netWeight, prefix, packagingTemplateId, setValue])

  const singleUnitOption = (id: number | null | undefined, unit: any) => {
    if (!id || !unit) return []
    const label = (unit.units ?? unit.name) as string | undefined
    if (!label) return []
    return [{ label, value: id }]
  }

  const massUnitOptions = useMemo(() => {
    if (skipMeasureUnits) {
      return singleUnitOption(physicalProperties?.weightMeasureUnitId, physicalProperties?.weightMeasureUnit)
    }
    return convertToLabelValue(
      measureUnits
        .filter((x: IMeasureUnit) => x.measureTypeId === 1)
        .map((x: IMeasureUnit) => {
          return { name: x.units, id: x.id }
        })
    )
  }, [measureUnits, skipMeasureUnits, physicalProperties?.weightMeasureUnitId, physicalProperties?.weightMeasureUnit])

  const lengthUnitOptions = useMemo(() => {
    if (skipMeasureUnits) {
      return singleUnitOption(physicalProperties?.lengthMeasureUnitId, physicalProperties?.lengthMeasureUnit)
    }
    return convertToLabelValue(
      measureUnits
        .filter((x: IMeasureUnit) => x.measureTypeId === 4)
        .map((x: IMeasureUnit) => {
          return { name: x.units, id: x.id }
        })
    )
  }, [measureUnits, skipMeasureUnits, physicalProperties?.lengthMeasureUnitId, physicalProperties?.lengthMeasureUnit])

  const volumeUnitOptions = useMemo(() => {
    if (skipMeasureUnits) {
      return singleUnitOption(physicalProperties?.volumeMeasureUnitId, physicalProperties?.volumeMeasureUnit)
    }
    return convertToLabelValue(
      measureUnits
        .filter((x: IMeasureUnit) => x.measureTypeId === 2)
        .map((x: IMeasureUnit) => {
          return { name: x.units, id: x.id }
        })
    )
  }, [measureUnits, skipMeasureUnits, physicalProperties?.volumeMeasureUnitId, physicalProperties?.volumeMeasureUnit])

  const densityUnitOptions = useMemo(() => {
    if (skipMeasureUnits) {
      return singleUnitOption(physicalProperties?.densityMeasureUnitId, physicalProperties?.densityMeasureUnit)
    }
    return convertToLabelValue(
      measureUnits
        .filter((x: IMeasureUnit) => x.measureTypeId === 6)
        .map((x: IMeasureUnit) => {
          return { name: x.units, id: x.id }
        })
    )
  }, [measureUnits, skipMeasureUnits, physicalProperties?.densityMeasureUnitId, physicalProperties?.densityMeasureUnit])

  const loadType = useMemo(
    () => loadTypes?.find((lt) => lt.id === selectedLoadTypeId),
    [loadTypes, selectedLoadTypeId]
  )

  const isContainer = useMemo(() => loadType?.name.toLowerCase() === 'container', [loadType])

  useEffect(() => {
    if (variant !== 'product' && variant !== 'loadTemplate' && variant !== 'asset' && !isVolume) {
      setValue(`${prefix}physicalProperties.useDimensions`, true, { shouldValidate: true })
    }
  }, [variant, isVolume, prefix, setValue])

  useEffect(() => {
    if ((variant === 'product' || variant === 'loadTemplate') && typeof isRectangular === 'undefined') {
      setValue(`${prefix}physicalProperties.isRectangular`, true)
    }
  }, [variant, isRectangular, prefix, setValue])


  const getUnit = useCallback(
    (id: number) => {
      const fromLookup = measureUnits.find((unit) => unit.id === id)?.units
      if (fromLookup) return fromLookup
      if (variant === 'product' && isDisabled) {
        const pp = physicalProperties || {}
        if (pp.weightMeasureUnitId === id) return pp.weightMeasureUnit?.units ?? pp.weightMeasureUnit?.name
        if (pp.lengthMeasureUnitId === id) return pp.lengthMeasureUnit?.units ?? pp.lengthMeasureUnit?.name
        if (pp.volumeMeasureUnitId === id) return pp.volumeMeasureUnit?.units ?? pp.volumeMeasureUnit?.name
        if (pp.densityMeasureUnitId === id) return pp.densityMeasureUnit?.units ?? pp.densityMeasureUnit?.name
      }
      return undefined
    },
    [measureUnits, variant, isDisabled, physicalProperties]
  )

  if (variant === 'loadTemplate') {
    const tareWeightLabel = isPallet ? 'Pallet Weight' : 'Tare Weight'
    return (
      <Box sx={{ width: '100%', p: '16px' }}>
        <DefaultSpacingGrid container>
          <Grid item xs={12} md={3}>
            <CombinedSelect
              nameValue={`${prefix}physicalProperties.netWeight`}
              nameUnit={`${prefix}physicalProperties.weightMeasureUnitId`}
              labelValue={tareWeightLabel}
              labelUnitValue={formatMessage(messages.uom)}
              optionsUnit={massUnitOptions}
              numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
              disabled={isDisabled}
              maxValue={99999999.9999}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormInput
              name={`${prefix}physicalProperties.grossWeight`}
              label='Max Gross Weight'
              type='number'
              unit={getUnit(physicalProperties.weightMeasureUnitId)}
              numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
              disabled={isDisabled}
              maxValue={99999999.9999}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormInput
              name={`${prefix}physicalProperties.chargeableWeight`}
              label='Payload Weight'
              type='number'
              unit={getUnit(physicalProperties.weightMeasureUnitId)}
              numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
              disabled={isDisabled}
              maxValue={99999999.9999}
            />
          </Grid>
        </DefaultSpacingGrid>

        <Box display='flex' alignItems='center' gap={2}>
          <Controller
            name={`${prefix}physicalProperties.useDimensions`}
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <SwitchWithLabels
                value={field.value}
                onChange={(checked) => {
                  field.onChange(checked)
                  if (checked) {
                    setValue(`${prefix}physicalProperties.volume`, '')
                    setValue(`${prefix}physicalProperties.volumeMeasureUnitId`, null)
                    setValue(`${prefix}physicalProperties.density`, '')
                    setValue(`${prefix}physicalProperties.densityMeasureUnitId`, null)
                    setValue(`${prefix}physicalProperties.isRectangular`, true)
                  } else {
                    setValue(`${prefix}physicalProperties.length`, '')
                    setValue(`${prefix}physicalProperties.width`, '')
                    setValue(`${prefix}physicalProperties.height`, '')
                    setValue(`${prefix}physicalProperties.lengthMeasureUnitId`, null)
                    setValue(`${prefix}physicalProperties.maxLoadingHeight`, '')
                    setValue(`${prefix}physicalProperties.isRectangular`, false)
                  }
                }}
                labelBefore='Volume'
                labelAfter='Dimensions'
                disabled={isDisabled}
              />
            )}
          />
          {effectiveUseDimensions && (
            <Controller
              name={`${prefix}physicalProperties.isRectangular`}
              control={control}
              render={({ field }) => (
                <SwitchWithLabels
                  value={field.value}
                  onChange={(checked) => {
                    field.onChange(checked)
                    setValue(`${prefix}physicalProperties.length`, '')
                    setValue(`${prefix}physicalProperties.width`, '')
                    setValue(`${prefix}physicalProperties.height`, '')
                    setValue(`${prefix}physicalProperties.lengthMeasureUnitId`, null)
                    setValue(`${prefix}physicalProperties.maxLoadingHeight`, '')
                  }}
                  labelBefore='Cylindrical'
                  labelAfter='Rectangle'
                  disabled={isDisabled}
                />
              )}
            />
          )}
        </Box>

        <DefaultSpacingGrid container sx={{ mt: 0 }}>
          {!effectiveUseDimensions ? (
            <>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.volume`}
                  nameUnit={`${prefix}physicalProperties.volumeMeasureUnitId`}
                  labelValue='Volume'
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={volumeUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.density`}
                  nameUnit={`${prefix}physicalProperties.densityMeasureUnitId`}
                  labelValue='Density'
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={densityUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                />
              </Grid>
            </>
          ) : effectiveIsRectangular ? (
            <>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.length`}
                  nameUnit={`${prefix}physicalProperties.lengthMeasureUnitId`}
                  labelValue='Length'
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={lengthUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`${prefix}physicalProperties.width`}
                  label='Width'
                  type='number'
                  unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`${prefix}physicalProperties.height`}
                  label='Height'
                  type='number'
                  unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                />
              </Grid>
              {isPallet && (
                <Grid item xs={12} md={3}>
                  <FormInput
                    name={`${prefix}physicalProperties.maxLoadingHeight`}
                    label='Max Loading Height'
                    type='number'
                    unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                    numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                    disabled={isDisabled}
                    maxValue={99999999.9999}
                  />
                </Grid>
              )}
            </>
          ) : (
            <>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.length`}
                  nameUnit={`${prefix}physicalProperties.lengthMeasureUnitId`}
                  labelValue='Diameter'
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={lengthUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`${prefix}physicalProperties.height`}
                  label='Height'
                  type='number'
                  unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                />
              </Grid>
            </>
          )}
        </DefaultSpacingGrid>
      </Box>
    )
  }

  if (variant === 'product') {
    const isRequiredLabel = watch(`${prefix}useProduct`)
    return (
      <Box sx={{ width: '100%', p: '16px' }}>
        <DefaultSpacingGrid container>
          <Grid item xs={12} md={3}>
            <CombinedSelect
              nameValue={`${prefix}physicalProperties.netWeight`}
              nameUnit={`${prefix}physicalProperties.weightMeasureUnitId`}
              labelValue='Net Weight'
              required={isRequiredLabel}
              labelUnitValue={formatMessage(messages.uom)}
              optionsUnit={massUnitOptions}
              numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
              disabled={isDisabled}
              isUnitDisabled={!!loadTemplateId}
              maxValue={99999999.9999}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormInput
              name={`${prefix}physicalProperties.grossWeight`}
              label='Gross Weight'
              type='number'
              unit={getUnit(physicalProperties.weightMeasureUnitId)}
              required={isRequiredLabel}
              numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
              disabled={isDisabled}
              maxValue={99999999.9999}
            />
          </Grid>
        </DefaultSpacingGrid>

        <Box display='flex' alignItems='center' gap={2}>
          <Controller
            name={`${prefix}physicalProperties.useDimensions`}
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <SwitchWithLabels
                value={field.value}
                onChange={(checked) => {
                  field.onChange(checked)
                  if (checked) {
                    setValue(`${prefix}physicalProperties.volume`, '')
                    setValue(`${prefix}physicalProperties.volumeMeasureUnitId`, null)
                    setValue(`${prefix}physicalProperties.density`, '')
                    setValue(`${prefix}physicalProperties.densityMeasureUnitId`, null)
                    setValue(`${prefix}physicalProperties.isRectangular`, true)
                  } else {
                    setValue(`${prefix}physicalProperties.length`, '')
                    setValue(`${prefix}physicalProperties.width`, '')
                    setValue(`${prefix}physicalProperties.height`, '')
                    setValue(`${prefix}physicalProperties.lengthMeasureUnitId`, null)
                    setValue(`${prefix}physicalProperties.isRectangular`, false)
                  }
                }}
                labelBefore='Volume'
                labelAfter='Dimensions'
                disabled={isDisabled}
              />
            )}
          />
          {effectiveUseDimensions && (
            <Controller
              name={`${prefix}physicalProperties.isRectangular`}
              control={control}
              render={({ field }) => (
                <SwitchWithLabels
                  value={field.value}
                  onChange={(checked) => {
                    field.onChange(checked)
                    setValue(`${prefix}physicalProperties.length`, '')
                    setValue(`${prefix}physicalProperties.width`, '')
                    setValue(`${prefix}physicalProperties.height`, '')
                    setValue(`${prefix}physicalProperties.lengthMeasureUnitId`, null)
                  }}
                  labelBefore='Cylindrical'
                  labelAfter='Rectangle'
                  disabled={isDisabled}
                />
              )}
            />
          )}
        </Box>

        <DefaultSpacingGrid container sx={{ mt: 0 }}>
          {!effectiveUseDimensions ? (
            <>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.volume`}
                  nameUnit={`${prefix}physicalProperties.volumeMeasureUnitId`}
                  labelValue='Volume'
                  required={isRequiredLabel}
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={volumeUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  isUnitDisabled={!!loadTemplateId}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.density`}
                  nameUnit={`${prefix}physicalProperties.densityMeasureUnitId`}
                  labelValue='Density'
                  required={isRequiredLabel}
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={densityUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  isUnitDisabled={!!loadTemplateId}
                  maxValue={99999999.9999}
                />
              </Grid>
            </>
          ) : effectiveIsRectangular ? (
            <>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.length`}
                  nameUnit={`${prefix}physicalProperties.lengthMeasureUnitId`}
                  labelValue='Length'
                  required={isRequiredLabel}
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={lengthUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  isUnitDisabled={!!loadTemplateId}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`${prefix}physicalProperties.width`}
                  label='Width'
                  type='number'
                  unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`${prefix}physicalProperties.height`}
                  label='Height'
                  type='number'
                  unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                  required={isRequiredLabel}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.length`}
                  nameUnit={`${prefix}physicalProperties.lengthMeasureUnitId`}
                  labelValue='Diameter'
                  required={isRequiredLabel}
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={lengthUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  isUnitDisabled={!!loadTemplateId}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`${prefix}physicalProperties.height`}
                  label='Height'
                  type='number'
                  unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                  required={isRequiredLabel}
                />
              </Grid>
            </>
          )}
        </DefaultSpacingGrid>
      </Box>
    )
  }

  if (variant === 'asset') {
    const isRequiredLabel = watch(`${prefix}useAsset`)
    const dimensionPrefix = showInternal ? 'internal' : 'external'
    const useDimensionsFieldName = `${prefix}physicalProperties.${dimensionPrefix}UseDimensions`
    const isRectangularFieldName = `${prefix}physicalProperties.${dimensionPrefix}IsRectangular`
    return (
      <Box sx={{ width: '100%', p: '16px' }}>
        <Box display='flex' alignItems='center' gap={2}>
          <Controller
            name={useDimensionsFieldName}
            control={control}
            defaultValue={true}
            render={({ field }) => (
              <SwitchWithLabels
                value={field.value}
                onChange={(checked) => {
                  field.onChange(checked)
                  if (checked) {
                    // Switching to Dimensions: clear this section's volume
                    setValue(`${prefix}physicalProperties.${dimensionPrefix}Volume`, '')
                    // Only clear shared volumeMeasureUnitId if the other section is not using Volume mode
                    const otherSectionUsesVolume = showInternal
                      ? !watch(`${prefix}physicalProperties.externalUseDimensions`)
                      : !watch(`${prefix}physicalProperties.internalUseDimensions`)
                    if (!otherSectionUsesVolume) {
                      setValue(`${prefix}physicalProperties.volumeMeasureUnitId`, null)
                    }
                    setValue(`${prefix}physicalProperties.${dimensionPrefix}IsRectangular`, true)
                  } else {
                    // Switching to Volume: clear this section's dimensions
                    setValue(`${prefix}physicalProperties.${dimensionPrefix}Length`, '')
                    setValue(`${prefix}physicalProperties.${dimensionPrefix}Width`, '')
                    setValue(`${prefix}physicalProperties.${dimensionPrefix}Height`, '')
                    if (showInternal) {
                      setValue(`${prefix}physicalProperties.clearLoadingHeight`, '')
                    }
                    // Only clear shared lengthMeasureUnitId if the other section is not using Dimensions mode
                    const otherSectionUsesDimensions = showInternal
                      ? watch(`${prefix}physicalProperties.externalUseDimensions`)
                      : watch(`${prefix}physicalProperties.internalUseDimensions`)
                    if (!otherSectionUsesDimensions) {
                      setValue(`${prefix}physicalProperties.lengthMeasureUnitId`, null)
                    }
                    setValue(`${prefix}physicalProperties.${dimensionPrefix}IsRectangular`, false)
                  }
                }}
                labelBefore='Volume'
                labelAfter='Dimensions'
                disabled={isDisabled}
              />
            )}
          />
          {effectiveUseDimensions && (
            <Controller
              name={isRectangularFieldName}
              control={control}
              render={({ field }) => (
                <SwitchWithLabels
                  value={field.value}
                  onChange={(checked) => {
                    field.onChange(checked)
                    setValue(`${prefix}physicalProperties.${dimensionPrefix}Length`, '')
                    setValue(`${prefix}physicalProperties.${dimensionPrefix}Width`, '')
                    setValue(`${prefix}physicalProperties.${dimensionPrefix}Height`, '')
                  }}
                  labelBefore='Cylindrical'
                  labelAfter='Rectangle'
                  disabled={isDisabled}
                />
              )}
            />
          )}
        </Box>

        <DefaultSpacingGrid container sx={{ mt: 0 }}>
          {!effectiveUseDimensions ? (
            <>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.${dimensionPrefix}Volume`}
                  nameUnit={`${prefix}physicalProperties.volumeMeasureUnitId`}
                  labelValue={'Volume'}
                  required={isRequiredLabel}
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={volumeUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  isUnitDisabled={!!loadTemplateId}
                  maxValue={99999999.9999}
                />
              </Grid>
            </>
          ) : effectiveIsRectangular ? (
            <>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.${dimensionPrefix}Length`}
                  nameUnit={`${prefix}physicalProperties.lengthMeasureUnitId`}
                  labelValue={'Length'}
                  required={isRequiredLabel}
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={lengthUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  isUnitDisabled={!!loadTemplateId}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`${prefix}physicalProperties.${dimensionPrefix}Width`}
                  label={'Width'}
                  type='number'
                  unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`${prefix}physicalProperties.${dimensionPrefix}Height`}
                  label={'Height'}
                  type='number'
                  unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                  required={isRequiredLabel}
                />
              </Grid>
              {showInternal && (
                <Grid item xs={12} md={3}>
                  <FormInput
                    name={`${prefix}physicalProperties.clearLoadingHeight`}
                    label={'Clear Loading Height'}
                    type='number'
                    unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                    numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                    disabled={isDisabled}
                    maxValue={99999999.9999}
                    required={isRequiredLabel}
                  />
                </Grid>
              )}
            </>
          ) : (
            <>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.${dimensionPrefix}Length`}
                  nameUnit={`${prefix}physicalProperties.lengthMeasureUnitId`}
                  labelValue={'Diameter'}
                  required={isRequiredLabel}
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={lengthUnitOptions}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  isUnitDisabled={!!loadTemplateId}
                  maxValue={99999999.9999}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`${prefix}physicalProperties.${dimensionPrefix}Height`}
                  label={'Height'}
                  type='number'
                  unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                  numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                  disabled={isDisabled}
                  maxValue={99999999.9999}
                  required={isRequiredLabel}
                />
              </Grid>
              {showInternal && (
                <Grid item xs={12} md={3}>
                  <FormInput
                    name={`${prefix}physicalProperties.clearLoadingHeight`}
                    label={'Clear Loading Height'}
                    type='number'
                    unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                    numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                    disabled={isDisabled}
                    maxValue={99999999.9999}
                    required={isRequiredLabel}
                  />
                </Grid>
              )}
            </>
          )}
        </DefaultSpacingGrid>
      </Box>
    )
  }

  if (variant === 'shipmentSummary') {
    const getUnit = (id: number | null, type: 'weight' | 'length' | 'volume') => {
      if (!id) return ''
      let units: any[] = []
      if (type === 'weight') units = massUnitOptions
      if (type === 'length') units = lengthUnitOptions
      if (type === 'volume') units = volumeUnitOptions
      const found = units.find((u) => u.value === id)
      return found ? found.label : ''
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pb: 0 }}>
        <DefaultSpacingGrid container>
          <Grid item xs={12} md={3}>
            <InfoNumberDisplayItem
              label={
                isParcel
                  ? formatMessage(messages.parcelWeight)
                  : isCrate
                    ? formatMessage(messages.crateWeight)
                    : formatMessage(messages.payloadWeight)
              }
              value={physicalProperties.netWeight}
              isSmallScreen={false}
              unit={getUnit(physicalProperties.weightMeasureUnitId, 'weight')}
              formatOptions={{
                minimumFractionDigits: 4,
                maximumFractionDigits: 4,
              }}
            />
          </Grid>
          {!isParcel && (
            <>
              <Grid item xs={12} md={3}>
                <InfoNumberDisplayItem
                  label={'Max Gross Weight'}
                  value={physicalProperties.grossWeight}
                  isSmallScreen={false}
                  unit={getUnit(physicalProperties.weightMeasureUnitId, 'weight')}
                  formatOptions={{
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InfoNumberDisplayItem
                  label={`${loadType?.name || 'Tare'} Weight`}
                  value={physicalProperties.tareWeight}
                  unit={getUnit(physicalProperties.weightMeasureUnitId, 'weight')}
                  isSmallScreen={false}
                  formatOptions={{
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  }}
                />
              </Grid>
            </>
          )}
        </DefaultSpacingGrid>
        <DefaultSpacingGrid container>
          {effectiveUseDimensions && (
            <>
              {effectiveIsRectangular ? (
                <>
                  <Grid item xs={12} md={3}>
                    <InfoNumberDisplayItem
                      label={'Length'}
                      value={physicalProperties.length}
                      isSmallScreen={false}
                      unit={getUnit(physicalProperties.lengthMeasureUnitId, 'length')}
                      formatOptions={{
                        minimumFractionDigits: 4,
                        maximumFractionDigits: 4,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <InfoNumberDisplayItem
                      label={'Width'}
                      value={physicalProperties.lengthMeasureUnitId}
                      unit={getUnit(physicalProperties.lengthMeasureUnitId, 'length')}
                      isSmallScreen={false}
                      formatOptions={{
                        minimumFractionDigits: 4,
                        maximumFractionDigits: 4,
                      }}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} md={3}>
                  <InfoNumberDisplayItem
                    label={'Diameter'}
                    value={physicalProperties.length}
                    isSmallScreen={false}
                    unit={getUnit(physicalProperties.lengthMeasureUnitId, 'length')}
                    formatOptions={{
                      minimumFractionDigits: 4,
                      maximumFractionDigits: 4,
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <InfoNumberDisplayItem
                  label={isPallet ? 'Max Loading Height' : 'Height'}
                  value={physicalProperties.height}
                  isSmallScreen={false}
                  unit={getUnit(physicalProperties.lengthMeasureUnitId, 'length')}
                  formatOptions={{
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  }}
                />
              </Grid>
            </>
          )}
          {!effectiveUseDimensions && !isContainer && (
            <Grid item xs={12} md={3}>
              <InfoNumberDisplayItem
                label={'Volume'}
                value={physicalProperties.volumeMeasureUnitId}
                isSmallScreen={false}
                formatOptions={{
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                }}
              />
            </Grid>
          )}
        </DefaultSpacingGrid>
      </Box>
    )
  }

  if (variant === 'contentSummary') {
    const getUnit = (id: number | null, type: 'weight' | 'length' | 'volume' | 'density') => {
      if (!id) return ''
      let units: any[] = []
      if (type === 'weight') units = massUnitOptions
      if (type === 'length') units = lengthUnitOptions
      if (type === 'volume') units = volumeUnitOptions
      if (type === 'density') units = densityUnitOptions
      const found = units.find((u) => u.value === id)
      return found ? found.label : ''
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pb: 0 }}>
        <DefaultSpacingGrid container>
          <Grid item xs={12} md={3}>
            <InfoNumberDisplayItem
              label={'Net Weight'}
              value={physicalProperties.netWeight}
              isSmallScreen={false}
              unit={getUnit(physicalProperties.weightMeasureUnitId, 'weight')}
              formatOptions={{
                minimumFractionDigits: 4,
                maximumFractionDigits: 4,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoNumberDisplayItem
              label={'Gross Weight'}
              value={physicalProperties.grossWeight}
              isSmallScreen={false}
              unit={getUnit(physicalProperties.weightMeasureUnitId, 'weight')}
              formatOptions={{
                minimumFractionDigits: 4,
                maximumFractionDigits: 4,
              }}
            />
          </Grid>
        </DefaultSpacingGrid>
        <DefaultSpacingGrid container>
          {!effectiveUseDimensions ? (
            <>
              <Grid item xs={12} md={3}>
                <InfoNumberDisplayItem
                  label={'Volume'}
                  value={physicalProperties.volume}
                  isSmallScreen={false}
                  unit={getUnit(physicalProperties.volumeMeasureUnitId, 'volume')}
                  formatOptions={{
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InfoNumberDisplayItem
                  label={'Density'}
                  value={physicalProperties.density}
                  isSmallScreen={false}
                  unit={getUnit(physicalProperties.densityMeasureUnitId, 'density')}
                  formatOptions={{
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  }}
                />
              </Grid>
            </>
          ) : effectiveIsRectangular ? (
            <>
              <Grid item xs={12} md={3}>
                <InfoNumberDisplayItem
                  label={'Length'}
                  value={physicalProperties.length}
                  isSmallScreen={false}
                  unit={getUnit(physicalProperties.lengthMeasureUnitId, 'length')}
                  formatOptions={{
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InfoNumberDisplayItem
                  label={'Width'}
                  value={physicalProperties.width}
                  isSmallScreen={false}
                  unit={getUnit(physicalProperties.lengthMeasureUnitId, 'length')}
                  formatOptions={{
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InfoNumberDisplayItem
                  label={'Height'}
                  value={physicalProperties.height}
                  isSmallScreen={false}
                  unit={getUnit(physicalProperties.lengthMeasureUnitId, 'length')}
                  formatOptions={{
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  }}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={3}>
                <InfoNumberDisplayItem
                  label={'Diameter'}
                  value={physicalProperties.length}
                  isSmallScreen={false}
                  unit={getUnit(physicalProperties.lengthMeasureUnitId, 'length')}
                  formatOptions={{
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InfoNumberDisplayItem
                  label={'Height'}
                  value={physicalProperties.height}
                  isSmallScreen={false}
                  unit={getUnit(physicalProperties.lengthMeasureUnitId, 'length')}
                  formatOptions={{
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  }}
                />
              </Grid>
            </>
          )}
        </DefaultSpacingGrid>
      </Box>
    )
  }

  return (
    <Box>
      <DefaultSpacingGrid container sx={{ mt: '0' }}>
        {isParcel ? (
          <Grid item xs={12} md={3}>
            <CombinedSelect
              nameValue={`${prefix}physicalProperties.netWeight`}
              nameUnit={`${prefix}physicalProperties.weightMeasureUnitId`}
              required={true}
              labelValue={
                isCrate ? formatMessage(messages.crateWeight) : formatMessage(messages.parcelWeight)
              }
              labelUnitValue={formatMessage(messages.uom)}
              optionsUnit={massUnitOptions}
              numberFormatProps={{
                decimalScale: 4,
                fixedDecimalScale: true,
              }}
              disabled={isDisabled}
              isUnitDisabled={!!loadTemplateId}
              maxValue={99999999.9999}
              onChange={(e) => {
                setValue(`${prefix}physicalProperties.grossWeight`, e.target.value)
                setValue(`${prefix}physicalProperties.tareWeight`, 0)
              }}
            />
          </Grid>
        ) : (
          <>
            <Grid item xs={12} md={3}>
              <CombinedSelect
                nameValue={`${prefix}physicalProperties.netWeight`}
                nameUnit={`${prefix}physicalProperties.weightMeasureUnitId`}
                required={true}
                labelValue={formatMessage(messages.payloadWeight)}
                labelUnitValue={formatMessage(messages.uom)}
                optionsUnit={massUnitOptions}
                numberFormatProps={{
                  decimalScale: 4,
                  fixedDecimalScale: true,
                }}
                disabled={isPayloadWeightDisabled}
                isUnitDisabled={!!loadTemplateId}
                maxValue={99999999.9999}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormInput
                name={`${prefix}physicalProperties.grossWeight`}
                required={true}
                label='Max Gross Weight'
                type='number'
                unit={getUnit(physicalProperties.weightMeasureUnitId)}
                numberFormatProps={{
                  decimalScale: 4,
                  fixedDecimalScale: true,
                }}
                disabled={isDisabled}
                maxValue={99999999.9999}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormInput
                name={`${prefix}physicalProperties.tareWeight`}
                required={true}
                label={`${loadType?.name || 'Tare'} Weight`}
                type='number'
                unit={getUnit(physicalProperties.weightMeasureUnitId)}
                numberFormatProps={{
                  decimalScale: 4,
                  fixedDecimalScale: true,
                }}
                disabled={true}
                maxValue={99999999.9999}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Box display='flex' alignItems='center' gap={2}>
            {!isContainer && isVolume && (
              <Controller
                name={`${prefix}physicalProperties.useDimensions`}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <SwitchWithLabels
                    value={field.value}
                    onChange={(checked) => {
                      field.onChange(checked)
                      if (checked) {
                        setValue(`${prefix}physicalProperties.volume`, null, {
                          shouldValidate: true,
                        })
                        setValue(`${prefix}physicalProperties.volumeMeasureUnitId`, null, {
                          shouldValidate: true,
                        })
                        setValue(`${prefix}physicalProperties.isRectangular`, true, {
                          shouldValidate: true,
                        })
                      } else {
                        setValue(`${prefix}physicalProperties.length`, null, {
                          shouldValidate: true,
                        })
                        setValue(`${prefix}physicalProperties.width`, null, {
                          shouldValidate: true,
                        })
                        setValue(`${prefix}physicalProperties.lengthMeasureUnitId`, null, {
                          shouldValidate: true,
                        })
                      }
                    }}
                    labelBefore='Volume'
                    labelAfter='Dimensions'
                    disabled={isDisabled}
                  />
                )}
              />
            )}

            {effectiveUseDimensions && selectedLoadTypeId === LoadType.parcel && (
              <Box display='flex' alignItems='center' gap={2}>
                <Controller
                  name={`${prefix}physicalProperties.isRectangular`}
                  control={control}
                  defaultValue={true}
                  render={({ field }) => (
                    <SwitchWithLabels
                      value={field.value}
                      onChange={(checked) => {
                        field.onChange(checked)
                        setValue(`${prefix}physicalProperties.length`, '')
                        setValue(`${prefix}physicalProperties.width`, '')
                        setValue(`${prefix}physicalProperties.height`, '')
                        setValue(`${prefix}physicalProperties.lengthMeasureUnitId`, null)
                      }}
                      labelBefore='Cylindrical'
                      labelAfter='Rectangle'
                      disabled={isDisabled}
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </Grid>
        {effectiveUseDimensions && (
          <Grid item xs={12}>
            <DefaultSpacingGrid container sx={{ mt: 0 }}>
              {effectiveIsRectangular && (
                <>
                  <Grid item xs={12} md={3}>
                    <CombinedSelect
                      nameValue={`${prefix}physicalProperties.length`}
                      nameUnit={`${prefix}physicalProperties.lengthMeasureUnitId`}
                      required={true}
                      labelValue='Length'
                      labelUnitValue={formatMessage(messages.uom)}
                      optionsUnit={lengthUnitOptions}
                      numberFormatProps={{
                        decimalScale: 4,
                        fixedDecimalScale: true,
                      }}
                      disabled={isDisabled}
                      isUnitDisabled={!!loadTemplateId}
                      maxLength={20}
                      maxValue={99999999.9999}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormInput
                      name={`${prefix}physicalProperties.width`}
                      required={true}
                      label='Width'
                      type='number'
                      unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                      numberFormatProps={{
                        decimalScale: 4,
                        fixedDecimalScale: true,
                      }}
                      disabled={isDisabled}
                      maxLength={20}
                      maxValue={99999999.9999}
                    />
                  </Grid>
                </>
              )}
              {!effectiveIsRectangular && (
                <Grid item xs={12} md={3}>
                  <CombinedSelect
                    nameValue={`${prefix}physicalProperties.length`}
                    nameUnit={`${prefix}physicalProperties.lengthMeasureUnitId`}
                    labelValue='Diameter'
                    required={isDisabled}
                    labelUnitValue={formatMessage(messages.uom)}
                    optionsUnit={lengthUnitOptions}
                    numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                    disabled={isDisabled}
                    isUnitDisabled={!!loadTemplateId}
                    maxValue={99999999.9999}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`${prefix}physicalProperties.height`}
                  required={true}
                  label={isPallet ? 'Max Loading Height' : 'Height'}
                  type='number'
                  unit={getUnit(physicalProperties.lengthMeasureUnitId)}
                  numberFormatProps={{
                    decimalScale: 4,
                    fixedDecimalScale: true,
                  }}
                  disabled={isDisabled}
                  maxLength={20}
                  maxValue={99999999.9999}
                />
              </Grid>
              {isPallet && (
                <Grid item xs={12} md={3}>
                  <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                    <Controller
                      name={`${prefix}physicalProperties.isStackable`}
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch {...field} checked={field.value} disabled={isDisabled} />
                          }
                          label='Stackable'
                        />
                      )}
                    />
                  </Box>
                </Grid>
              )}
            </DefaultSpacingGrid>
          </Grid>
        )}

        {!effectiveUseDimensions && !isContainer && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <CombinedSelect
                  nameValue={`${prefix}physicalProperties.volume`}
                  nameUnit={`${prefix}physicalProperties.volumeMeasureUnitId`}
                  required={true}
                  labelValue='Volume'
                  labelUnitValue={formatMessage(messages.uom)}
                  optionsUnit={volumeUnitOptions}
                  numberFormatProps={{
                    decimalScale: 4,
                    fixedDecimalScale: true,
                  }}
                  disabled={isDisabled}
                  isUnitDisabled={!!loadTemplateId}
                  maxLength={20}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </DefaultSpacingGrid>
    </Box>
  )
}

export default SharedPhysicalProperties
